import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import WaitlistModal from "@/components/WaitlistModal";

const SECTIONS = [
  { id: "acceptance", title: "1. Acceptance of Terms" },
  { id: "what-we-do", title: "2. What LaunchRadar Does" },
  { id: "account-registration", title: "3. Account Registration" },
  { id: "admin-visibility", title: "4. Admin Visibility of Your Project Data" },
  { id: "seed-urls", title: "5. Project Configuration and Seed URLs" },
  { id: "third-party-content", title: "6. Third-Party Content" },
  { id: "ai-summaries", title: "7. AI-Generated Summaries" },
  { id: "not-advice", title: "8. Not Professional Advice" },
  { id: "communication-consent", title: "9. Communication Consent" },
  { id: "acceptable-use", title: "10. Acceptable Use" },
  { id: "ip", title: "11. Intellectual Property" },
  { id: "data-privacy", title: "12. Data Privacy" },
  { id: "financial-terms", title: "13. Financial Terms" },
  { id: "suspension", title: "14. Account Suspension and Termination" },
  { id: "liability", title: "15. Warranty Disclaimer & Liability" },
  { id: "changes", title: "16. Changes to These Terms" },
  { id: "dispute-resolution", title: "17. Dispute Resolution" },
  { id: "governing-law", title: "18. Governing Law" },
  { id: "third-party-services", title: "19. Third-Party Services" },
  { id: "general", title: "20. General" },
  { id: "contact", title: "21. Contact" },
];

export default function TermsConditions() {
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        // Trigger when a section heading is near the top of the viewport
        rootMargin: "-100px 0px -70% 0px",
        threshold: 0,
      }
    );

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -88; // account for fixed header
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC] text-[#0F1F3D]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="LaunchRadar"
              className="w-[200px]"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm text-[#475569]">
            <Link to="/" className="hover:text-[#0F1F3D] transition-colors">
              Use cases
            </Link>
            <Link to="/" className="hover:text-[#0F1F3D] transition-colors">
              How it works
            </Link>
            <Link to="/" className="hover:text-[#0F1F3D] transition-colors">
              Why us
            </Link>
            <Link
              to="/contact"
              className="hover:text-[#0F1F3D] transition-colors"
            >
              Contact
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm text-[#475569] hover:text-[#0F1F3D]"
            >
              Sign in
            </Link>

            <button
              type="button"
              onClick={() => setShowWaitlist(true)}
              className="px-4 py-2 rounded-md bg-[#0F1F3D] text-white text-sm font-medium hover:bg-[#172A4F]"
            >
              Join Waitlist
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl w-full mx-auto px-6 pt-32 pb-16 flex-1">
        <div className="mb-10 border-b border-[#E2E8F0] pb-8">
          <span className="inline-flex items-center rounded-full border border-[#E2E8F0] bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#64748B]">
            Legal
          </span>

          <h1 className="mt-5 text-5xl font-display font-bold tracking-tight">
            Terms &amp; Conditions
          </h1>

          <p className="mt-3 text-[#64748B] text-sm">
            LaunchRadar — operated by OneRoof Launchpad
            <br />
            Last updated: June 2026
          </p>
        </div>

        <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-10 xl:gap-14">
          {/* Sidebar Table of Contents */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#94A3B8] mb-4">
                On this page
              </p>
              <nav className="max-h-[calc(100vh-140px)] overflow-y-auto pr-2">
                <ul className="space-y-1 border-l border-[#E2E8F0]">
                  {SECTIONS.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                      <li key={section.id}>
                        <button
                          type="button"
                          onClick={() => scrollToSection(section.id)}
                          className={`block w-full text-left text-sm pl-4 py-1.5 -ml-px border-l-2 transition-colors ${
                            isActive
                              ? "border-[#0F1F3D] text-[#0F1F3D] font-medium"
                              : "border-transparent text-[#64748B] hover:text-[#0F1F3D] hover:border-[#CBD5E1]"
                          }`}
                        >
                          {section.title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <div className="rounded-2xl border border-[#E2E8F0] bg-white shadow-sm p-8 md:p-12 space-y-6 text-sm">
            <section id="acceptance" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                1. Acceptance of Terms
              </h2>

              <p className="text-[#475569] ">
                By creating an account or using LaunchRadar (the Service), you agree to these Terms and Conditions (Terms).<br /><br />
                By accepting these Terms, you confirm that you are at least 18 years old, or that you have the verifiable consent of a parent or legal guardian to use the Service. LaunchRadar is not directed at children.<br />
                If you are using the Service on behalf of an organisation, you confirm that you have authority to bind that organisation to these Terms. If you lack such authority, you accept these Terms in your personal capacity and are personally responsible for compliance.<br /><br />
                These Terms are provided in English. Where required by applicable law, including Section 6(3) of India's DPDP Act, users may request notices in another applicable language. In the event of any inconsistency between versions, the English version prevails, except where mandatory law provides otherwise.<br /><br />
                If you do not agree, do not use the Service.

              </p>
            </section>

            <section id="what-we-do" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                2. What LaunchRadar Does
              </h2>

              <p className="text-[#475569] ">
                LaunchRadar is an intelligence monitoring tool for business, professional, and personal use. It allows users to define project contexts, including sectors, keywords, geographies, and target entities, and receive ranked, summarised signals drawn from publicly available sources on the open web. Signals are delivered via an in-app feed, email digest, and WhatsApp (where enabled).<br /><br />
                Signals produced by LaunchRadar include summaries, relevance scores, and recommended next steps generated by an artificial intelligence system. This is disclosed in accordance with Article 50 of the EU AI Act.
              </p>
            </section>

            <section id="account-registration" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                3. Account Registration
              </h2>

              <p className="text-[#475569] ">
                You must provide accurate information when creating your account. You agree to keep your account information accurate and up to date, and to notify us of any material changes.<br /><br />
                You may not share account credentials with any other person or create an account on behalf of anyone else without their explicit consent and authority to do so.<br /><br />
                ORL is responsible for implementing appropriate technical and organisational measures to secure the Service in accordance with Article 32 GDPR. You are responsible for the security of your own account credentials and devices. LaunchRadar is not liable for loss or damage resulting from unauthorised access to your account caused by your failure to safeguard your credentials.

              </p>
            </section>

            <section id="admin-visibility" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                4. Admin Visibility of Your Project Data
              </h2>

              <p className="text-[#475569] font-semibold">
                Please read this section carefully.
              </p>

              <p className="text-[#475569] ">
                LaunchRadar is operated by OneRoof Launchpad ("ORL"). ORL personnel with production database access are able to view all data stored on the platform. In practice this means our administrators can access:
              </p>

              <ul className="list-disc pl-6 text-[#475569]  space-y-2">
                <li>Your account email and organisation</li>
                <li>Your project names, descriptions, and relevance briefs</li>
                <li>Keywords, sectors, geographies, seed URLs, and target entities you have added</li>
                <li>Your usage activity, credits used, and signal history</li>
              </ul>

              <p className="text-[#475569] ">
                This access exists for the purposes of platform support, quality assurance, abuse prevention, and service improvement. ORL does not sell or share your project data with third parties for commercial purposes.<br /><br />
                Administrative access is restricted to authorised personnel bound by confidentiality obligations, and is logged.<br /><br />
                You acknowledge that this administrative visibility is a necessary condition of operating the Service. The lawful basis for this processing under GDPR (contract and legitimate interests) and the DPDP Act is set out in our Privacy Policy.

              </p>
            </section>

            <section id="seed-urls" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                5. Project Configuration and Seed URLs
              </h2>

              <p className="text-[#475569] ">
                When configuring a project, you may add URLs for the pipeline to scrape directly ("seed URLs"). By submitting a URL, you confirm that:
              </p>

              <ul className="list-disc pl-6 text-[#475569]  space-y-2">
                <li>You are not directing the Service to access content that is password-protected, paywalled, or explicitly restricted from automated access.</li>
                <li>You have a legitimate purpose for monitoring that source, whether business, professional, or personal.</li>
                <li>You are not using the Service to circumvent access controls or violate the terms of service of third-party websites.</li>
                <li>You are not entering personal data of identifiable individuals, including names, email addresses, phone numbers, or profiles of specific persons, into project fields or seed
                  URLs, except where strictly necessary for a lawful monitoring purpose and where you have a valid legal basis under applicable data protection law for doing so.</li>
              </ul>

              <p className="text-[#475569] ">
                You are responsible for ensuring that your monitoring configuration complies with GDPR, the DPDP Act, and any other applicable data protection law. ORL respects robots.txt and machine-readable text-and-data-mining reservations made by publishers under Article 4 of the EU Copyright Directive (2019/790).<br /><br />
                You indemnify ORL against any claim arising from a seed URL you submit that violates third-party rights or applicable law.<br /><br />
                ORL reserves the right to remove seed URLs that it determines to be in violation of this clause.
              </p>
            </section>

            <section id="third-party-content" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                6. Third-Party Content
              </h2>

              <p className="text-[#475569] ">
                The signals surfaced by LaunchRadar are drawn from publicly available third-party sources including news publications, government websites, procurement portals, and regulatory databases. ORL does not own, control, or endorse this content.

              </p>

              <ul className="list-disc pl-6 text-[#475569]  space-y-2">
                <li>ORL does not guarantee the accuracy, completeness, or timeliness of third-party content.</li>
                <li>Content is provided for informational purposes only.</li>
                <li>LaunchRadar surfaces summaries and links, not reproduced full-text articles. Original copyright remains with the respective publishers.</li>
              </ul>

              <p className="text-[#475569] ">
                Where applicable, ORL relies on the exceptions for text and data mining (Article 4 EU Copyright Directive) and for lawful uses of publicly available content.<br /><br />
                If you are a rights holder and believe your content is being used inappropriately by the Service, contact <Link href="mailto:core@onerooflaunchpad.com" className="text-[#0EA5E9] hover:underline">core@onerooflaunchpad.com</Link>. ORL will review and, where appropriate, remove or restrict the relevant content.

              </p>
            </section>

            <section id="ai-summaries" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                7. AI-Generated Summaries and Third-Party AI Processing
              </h2>

              <p className="text-[#475569] ">
                LaunchRadar uses artificial intelligence to generate summaries, relevance scores, and recommended next steps for each signal. These are automated outputs and may include inaccuracies, hallucinations, or fabricated references.
              </p>

              <ul className="list-disc pl-6 text-[#475569]  space-y-2">
                <li>AI-generated summaries are not a substitute for reading source material directly.</li>
                <li>Relevance scores reflect the system's interpretation of your project context and are not guaranteed to be accurate.</li>
                <li>You must verify any material information against the underlying source before acting on it.</li>
                <li>ORL is not liable for decisions made on the basis of AI-generated content.</li>
              </ul>

              <p className="text-[#475569] ">
                <b>Third-party AI infrastructure:</b>
                <br /><br />

                The AI processing that powers LaunchRadar's summarisation and scoring is currently provided by a third-party AI infrastructure. In order to generate results, elements of your project configuration, including keywords, sector descriptions, and relevance briefs are transmitted to the provider's systems for processing. The provider operates under its own terms of service and data processing agreements, and ORL ensures that any such provider meets standards consistent with applicable data protection law.<br /><br />
                ORL does not permit its AI provider to use your project configuration or content generated for you to train foundation models.<br /><br />
                ORL does not transmit personally identifiable information to its AI provider beyond what is contained in your project configuration. You should avoid entering personal data, such as individual names or personal contact details, into project fields unless necessary for your monitoring purpose.<br /><br />
                ORL will update its Privacy Policy to reflect any material changes to the AI infrastructure it uses.
              </p>
            </section>

            <section id="not-advice" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                8. Not Professional Advice
              </h2>

              <p className="text-[#475569] ">
                Nothing on LaunchRadar constitutes legal, financial, regulatory, investment, tax, medical, immigration, or professional advice of any kind. Signals, summaries, and recommendations provided by the Service are for general informational purposes only.<br /><br />
                If you require advice on regulatory compliance, trade law, financial matters, or any other professional domain, you should consult a qualified professional.
              </p>
            </section>

            <section id="communication-consent" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                9. Communication Consent (Email and WhatsApp)
              </h2>

              <p className="text-[#475569] ">
                Email and WhatsApp delivery are optional features that require your explicit, separate consent per channel. You will be asked to opt in to each channel independently before any messages are sent. Enabling delivery is not a condition of using the core Service.<br /><br />
                You may withdraw consent at any time via project settings, the unsubscribe link in each email, replying STOP to a WhatsApp message, or by contacting support. Withdrawal of consent does not affect the lawfulness of processing carried out before withdrawal, and does not delete your account or project data.<br /><br />
                WhatsApp delivery is additionally subject to WhatsApp's Business Solution Terms and Meta's data policies. By opting in to WhatsApp delivery, you also agree to those terms.

              </p>
            </section>

            <section id="acceptable-use" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                10. Acceptable Use
              </h2>

              <p className="text-[#475569] ">
                You agree not to use the Service to:
              </p>

              <ul className="list-disc pl-6 text-[#475569]  space-y-2">
                <li>Monitor individuals without their knowledge in a manner that violates applicable privacy law.</li>
                <li>Profile, score, or make automated decisions about individual natural persons in a manner that would be prohibited under Article 5 of the EU AI Act.</li>
                <li>Conduct surveillance of individuals in the workplace, of students, or of vulnerable groups.</li>
                <li>Target, track, or monitor children (defined as under 16 in the Netherlands and under 18 in India), or use the Service for behavioural monitoring or targeted advertising directed at children.</li>
                <li>Engage in competitive intelligence gathering in violation of any applicable law or third-party terms.</li>
                <li>Attempt to reverse-engineer, scrape, or extract the LaunchRadar platform itself.</li>
                <li>Circumvent or interfere with the technical operation of the Service.</li>
                <li>Submit false, misleading, or harmful project configurations.</li>
                <li>Use the Service in violation of any applicable trade, sanctions, or export-control law, including EU and Dutch sanctions regimes and Indian export-control law.</li>
                <li>Use the Service for any unlawful purpose.</li>
              </ul>

              <p className="text-[#475569] ">
                ORL reserves the right to suspend or terminate accounts that violate this clause without prior notice.
              </p>
            </section>

            <section id="ip" className="space-y-6 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                11. Intellectual Property
              </h2>

              <div className="space-y-4 text-[#475569]">
                <p>
                  <strong className="text-[#0F1F3D]">ORL's IP:</strong>{" "}
                  The LaunchRadar platform, its pipeline, scoring methodology, interface,
                  and all related technology are the intellectual property of OneRoof
                  Launchpad. You may not reproduce, distribute, or create derivative works
                  from any part of the platform.
                </p>

                <p>
                  <strong className="text-[#0F1F3D]">Your IP:</strong>{" "}
                  The project names, descriptions, keywords, and context you configure
                  remain yours. You grant ORL a worldwide, non-exclusive, royalty-free
                  licence to process the project configuration data you provide, solely for
                  the purpose of operating, maintaining, and improving the Service. This
                  licence terminates when you delete your account or the relevant project,
                  subject to statutory retention obligations set out in the Privacy Policy.
                </p>

                <p>
                  <strong className="text-[#0F1F3D]">Feedback:</strong>{" "}
                  If you provide feedback or suggestions to ORL, you grant ORL a perpetual,
                  irrevocable, royalty-free licence to use that feedback without obligation
                  to you.
                </p>

                <p>
                  <strong className="text-[#0F1F3D]">Third-party IP:</strong>{" "}
                  Certain functionality is powered by third-party services. Those services
                  remain the intellectual property of their respective owners.
                </p>

                <div>
                  <p>
                    <strong className="text-[#0F1F3D]">Trademarks:</strong>{" "}
                    The names "LaunchRadar" and{" "}
                    "OneRoof Launchpad", along with their respective logos
                    and brand identifiers, are trademarks of ORL. You may not use these
                    names, logos, or any confusingly similar marks in any of the following
                    ways without ORL's prior written consent:
                  </p>

                  <ul className="mt-3 list-disc pl-6 leading-6">
                    <li>In your own product or service name.</li>
                    <li>In advertising, marketing, or promotional materials.</li>
                    <li>
                      In any way that implies endorsement, sponsorship, or affiliation with
                      ORL.
                    </li>
                    <li>
                      As a meta-tag, keyword, or search term in any online advertising
                      platform.
                    </li>
                  </ul>
                </div>

                <p>
                  <strong className="text-[#0F1F3D]">Permitted use:</strong>{" "}
                  You may refer to LaunchRadar by name in a factual, descriptive context,
                  for example, <em>"we use LaunchRadar to monitor market signals"</em>,
                  provided the reference is accurate and does not imply any endorsement or
                  partnership beyond your use of the Service.
                </p>
              </div>
            </section>

            <section id="data-privacy" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                12. Data Privacy
              </h2>

              <p className="text-[#475569] ">
                ORL processes your personal data in accordance with its{" "}
                <a  href="/privacy-policy"
                  className="font-medium text-[#0F1F3D] underline underline-offset-4 hover:text-[#10B981]"
                >
                  Privacy Policy
                </a>. By using the Service, you acknowledge that your personal data will be processed as described in the Privacy Policy.<br /><br />
                The Service is operated from the Netherlands and complies with the EU General Data Protection Regulation (GDPR), the Dutch Uitvoeringswet AVG (UAVG), the ePrivacy provisions of the Dutch Telecommunicatiewet, and, in respect of users in India, India's Digital Personal Data Protection Act, 2023 (DPDP Act).<br /><br />
                For data protection concerns and grievances, including under Section 8(9) of the DPDP Act, contact the Grievance Officer as set out in the Privacy Policy and in Section 21 below.

              </p>
            </section>

            <section id="financial-terms" className="space-y-6 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                13. Financial Terms
              </h2>

              <div className="space-y-4 text-[#475569]">
                <p>
                  <strong className="text-[#0F1F3D]">Current free tier:</strong>{" "}
                  LaunchRadar is currently available free of charge. Access to the Service at no cost does not constitute a promise of continued free access. ORL reserves the right to introduce paid plans at any time, subject to the notice requirements set out below.
                </p>

                <p>
                  <strong className="text-[#0F1F3D]">Introduction of paid plans:</strong>{" "}
                  If ORL introduces paid tiers or moves features currently available for free behind a paywall, ORL will notify existing users by email at least 30 days before the change takes effect. You will not be automatically charged, any transition to a paid plan requires your explicit opt-in.
                </p>

                <p>
                  <strong className="text-[#0F1F3D]">Feedback:</strong>{" "}
                  If you provide feedback or suggestions to ORL, you grant ORL a perpetual,
                  irrevocable, royalty-free licence to use that feedback without obligation
                  to you.
                </p>

                <p>
                  <strong className="text-[#0F1F3D]">Third-party IP:</strong>{" "}
                  Certain functionality is powered by third-party services. Those services
                  remain the intellectual property of their respective owners.
                </p>

                <div>
                  <p>
                    <strong className="text-[#0F1F3D]">Paid subscriptions (when introduced):</strong>{" "}
                  </p>

                  <ul className="mt-3 list-disc pl-6 leading-6">
                    <li>Subscription fees will be stated clearly at the point of purchase, inclusive of any applicable taxes.</li>
                    <li>Prices displayed to EU consumers are inclusive of applicable VAT. Business users with a valid EU VAT number outside the Netherlands may qualify for the reverse charge mechanism.</li>
                    <li>For paid services provided to Indian users, applicable Indian taxes (including GST under the OIDAR regime, where applicable) will be added at checkout.</li>
                    <li>Subscriptions renew automatically at the end of each billing period unless cancelled before the renewal date. Auto-renewal will be prominently disclosed at checkout, and you will receive a renewal reminder by email at least 14 days before each renewal.</li>
                    <li>You may cancel your subscription at any time via your account settings. Cancellation takes effect at the end of the current billing period, you retain access until that date and will not be charged for the next period.</li>
                    <li>ORL reserves the right to change subscription pricing with at least 30 days' written notice. Continued use of the Service after a price change takes effect constitutes acceptance of the new pricing.</li>
                    <li>Payment processing is handled by a third-party PCI-DSS compliant payment processor. ORL does not store your full payment card details.</li>

                  </ul>
                </div>

                <div>
                  <p>
                    <strong className="text-[#0F1F3D]">Refund policy:</strong>{" "}
                  </p>

                  <ul className="mt-3 list-disc pl-6 leading-6">
                    <li>Free tier: no charges apply, so no refunds are applicable.</li>
                    <li>Paid plans: ORL does not offer refunds for partial billing periods or unused time remaining after cancellation, except where required by applicable law.</li>
                    <li>If you believe you have been charged in error, contact ORL within 30 days of the charge. ORL will investigate and, where an error is confirmed, issue a full refund of the incorrect amount.</li>
                    <li>EU consumers have the right to withdraw from a paid contract within 14 days of the purchase date under the Consumer Rights Directive. If you request that the Service begin during this period, you acknowledge that you will lose the right of withdrawal once the Service has been fully performed. Any pro-rata refund for services partially used will be calculated based on actual use. To exercise this right, contact ORL at the address in Section 21.</li>
                  </ul>
                </div>

                <p>
                  <strong className="text-[#0F1F3D]">Failed payments:</strong>{" "}
                  If a payment fails on a paid plan, ORL will notify you and allow a grace period of 7 days to update your payment details. If payment is not resolved within the grace period, ORL may downgrade your account to the free tier or suspend access until payment is resolved.
                </p>

                <p>
                  <strong className="text-[#0F1F3D]">Taxes:</strong>{" "}
                  You are responsible for any taxes, duties, or levies applicable to your use of the Service in your jurisdiction, beyond those collected by ORL at the point of sale.
                </p>
              </div>
            </section>

            <section id="suspension" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                14. Account Suspension and Termination
              </h2>

              <div>
                <p>
                  <strong className="text-[#0F1F3D]">By you:</strong>{" "}<br />
                  You may delete your account at any time via account settings. Before account deletion takes effect, you may request an export of your data in a structured, commonly used, machine-readable format via account settings or by contacting us. Upon deletion, your data is handled in accordance with the retention schedule set out in our Privacy Policy, project configurations and signal history are permanently removed within 30 days, with residual data in encrypted backups overwritten within a further 90 days.
                </p>
              </div>

              <div>
                <p>
                  <strong className="text-[#0F1F3D]">By ORL:</strong><br />
                  ORL may suspend or terminate your account if:
                </p>

                <ul className="mt-3 list-disc pl-6 leading-6">
                  <li>You materially breach these Terms;</li>
                  <li>Required by law or court order;</li>
                  <li>Your account has been inactive for 24 consecutive months (inactive accounts may be suspended after 30 days' email notice and deleted after a further 90 days);</li>
                  <li>ORL discontinues the Service, with at least 30 days' notice; or</li>
                  <li>Continued provision of the Service to you would expose ORL to material legal risk.</li>
                </ul>

                <p className="text-[#475569] ">
                  Where possible, ORL will provide reasonable notice before termination.
                </p>
              </div>
            </section>

            <section id="liability" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                15. Warranty Disclaimer and Limitation of Liability
              </h2>

              <div>
                <p>
                  <strong className="text-[#0F1F3D]">Warranty disclaimer:</strong>{" "}<br />
                  The Service is provided "as is" and "as available," without warranty of any kind. To the maximum extent permitted by applicable law, ORL expressly disclaims all warranties, whether express, implied, or statutory, including but not limited to:
                </p>
                <ul className="mt-3 list-disc pl-6 leading-6">
                  <li>Any implied warranty of merchantability, fitness for a particular purpose, or non-infringement.</li>
                  <li>Any warranty that the Service will be uninterrupted, error-free, or free of harmful components.</li>
                  <li>Any warranty that signals, summaries, or results produced by the Service will be accurate, complete, timely, or suitable for your specific business needs.</li>
                  <li>Any warranty that the Service will meet your requirements or that defects will be corrected.</li>
                </ul>

                <p className="text-[#475569] ">
                  Your use of the Service is entirely at your own risk. ORL does not warrant that the pipeline will find all relevant signals, that no signals will be missed, or that the relevance scoring will align with your expectations.
                </p>
              </div>

              <div>
                <p>
                  <strong className="text-[#0F1F3D]">Limitation of liability:</strong>{" "}<br />
                  To the maximum extent permitted by applicable law:
                </p>
                <ul className="mt-3 list-disc pl-6 leading-6">
                  <li>ORL's total liability to you for any claim arising out of or relating to the Service shall not exceed the amount you paid to ORL in the three months preceding the claim.</li>
                  <li>ORL is not liable for indirect, incidental, consequential, or special damages, including loss of profits, loss of data, or business interruption.</li>
                  <li>ORL is not liable for the accuracy or completeness of any signal, summary, or third-party content surfaced by the Service.</li>
                  <li>ORL is not liable for any business decision made in reliance on content, signals, or recommendations produced by the Service.</li>
                </ul>
              </div>

              <div>
                <p>
                  <strong className="text-[#0F1F3D]">Consumer protection:</strong>{" "}<br />
                  The limitations in this Section apply to business users only. For EU consumers, ORL's liability is limited only to the extent permitted by applicable Dutch and EU consumer law, and nothing in these Terms limits or excludes any statutory right that cannot be limited or excluded, including implied warranties of conformity under Book 7 of the Dutch Civil Code and Directive (EU) 2019/770.
                </p>
              </div>

              <div>
                <p>
                  <strong className="text-[#0F1F3D]">Data protection liability:</strong>{" "}<br />
                  Nothing in this Section limits ORL's liability under Article 82 GDPR, Section 39 of the DPDP Act, or under any other law that does not permit such limitation. Nothing in this Section limits liability for fraud or gross negligence.
                </p>
              </div>

              <div>
                <p>
                  <strong className="text-[#0F1F3D]">Force majeure:</strong>{" "}<br />
                  ORL is not liable for failure to perform where such failure results from events beyond its reasonable control, including internet or infrastructure outages, actions of third-party providers, natural disasters, cyberattacks, or acts of government.
                </p>
              </div>
            </section>

            <section id="changes" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                16. Changes to These Terms

              </h2>

              <p className="text-[#475569] ">
                For material changes that affect your rights or obligations under these Terms, including price changes on paid plans, ORL will provide at least 30 days' notice by email. During this period, you may terminate your account without penalty if you do not agree to the changes. For non-material changes, ORL will provide at least 14 days' notice by email.<br /><br />
                Where a change materially affects how we process your personal data, we will seek fresh consent from users where required by applicable law.<br /><br />
                The current and preceding version of these Terms will always be available here.

              </p>
            </section>

            <section id="dispute-resolution" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                17. Dispute Resolution
              </h2>

              <p className="text-[#475569] ">
                <b>Step 1 — Contact ORL first:</b> Before initiating any formal legal proceedings, you agree to give ORL the opportunity to resolve the dispute informally. To do this, send a written notice to ORL at the contact address in Section 21, describing the nature of the dispute, the outcome you are seeking, and any relevant supporting information. ORL will acknowledge your notice within 5 business days and respond substantively within 30 days.<br /><br />
                <b>Step 2 — Good faith negotiation:</b> Both parties agree to attempt resolution in good faith during the 30-day response period. Either party may request a call or meeting during this period. Neither party may initiate formal proceedings until the 30-day period has elapsed, unless urgent interim relief is required to prevent imminent harm.<br /><br />
                <b>Step 3 — Mediation (optional):</b> If the dispute is not resolved through informal negotiation, either party may request non-binding mediation before a mutually agreed mediator. The costs of mediation shall be shared equally between the parties unless otherwise agreed. Mediation is voluntary — if either party declines or mediation fails to produce agreement, either party may proceed to formal proceedings.<br /><br />
                <b>Step 4 — Formal proceedings:</b> If the dispute remains unresolved after the steps above, it shall be subject to the jurisdiction set out in Section 18.
                <b>Consumers:</b> The pre-litigation steps in this Section do not apply to EU consumers, who retain the right to bring proceedings directly. Indian consumers may exercise rights available under India's Consumer Protection Act, 2019, and may pursue remedies through the relevant Consumer Commission where applicable. The pre-litigation steps apply to business users.<br /><br />
                <b>Data protection complaints:</b> For data protection complaints, users may contact our Grievance Officer (Section 21). Users in the EEA may lodge a complaint with the Autoriteit Persoonsgegevens or their local supervisory authority. Users in India may escalate to the Data Protection Board of India.<br /><br />
                <b>Exceptions:</b> Nothing in this clause prevents either party from seeking urgent injunctive or interim relief from a court of competent jurisdiction where delay would cause irreparable harm, for example, in cases of intellectual property infringement or unauthorised data access.<br /><br />
                <b>EU consumers ODR:</b> If you are a consumer based in the European Union, you may also submit a complaint through the European Commission's Online Dispute Resolution platform at https://ec.europa.eu/consumers/odr. ORL is not obligated to participate in online dispute resolution but will consider any complaint submitted through this channel.

              </p>
            </section>
            <section id="governing-law" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                18. Governing Law
              </h2>

              <p className="text-[#475569] ">
                These Terms are governed by the laws of the Netherlands. Any disputes arising from or related to these Terms or the Service that are not resolved through the process set out in Section 17 shall be subject to the exclusive jurisdiction of the courts of Rotterdam, the Netherlands.<br/><br/>
                Nothing in this Section deprives EU consumers of the protection of the mandatory provisions of the law of the country in which they habitually reside, in accordance with Article 6 of the Rome I Regulation. Nothing in this Section deprives Indian consumers of protections mandated under applicable Indian consumer or data protection law.
              </p>
            </section>
            <section id="third-party-services" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                19. Third-Party Services
              </h2>

              <p className="text-[#475569] ">
                The Service integrates with third-party services, including AI provider, cloud hosting providers, email delivery providers, and WhatsApp Business API providers. Your use of features that depend on those services is subject to their respective terms. ORL is not responsible for the availability or performance of third-party services, but will use reasonable efforts to select providers that meet standards consistent with applicable data protection and security law.
              </p>
            </section>

            <section id="general" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                20. General
              </h2>

              <p className="text-[#475569] ">
                <b>Assignment:</b>{" "}<br />
                ORL may assign these Terms and its rights and obligations under them to a successor entity in the event of a merger, acquisition, or sale of assets, subject to at least 30 days' prior notice to you. You may not assign these Terms without ORL's prior written consent.
                <br /><br />
                <b>Severability:</b><br />
                If any provision of these Terms is held to be unlawful or unenforceable, the remaining provisions remain in full force and effect.
                <br /><br />
                <b>Entire agreement:</b><br />
                These Terms, together with the Privacy Policy, constitute the entire agreement between you and ORL in relation to the Service, and supersede any prior agreements.
                <br /><br />
                <b>No waiver:</b><br />
                Failure by ORL to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.<br /><br />
                <b>Notices:</b><br />
                ORL will send notices to you via the email address associated with your account, and via in-app notification where appropriate. Notices to ORL must be sent to core@onerooflaunchpad.com.

              </p>
            </section>
            <section id="contact" className="space-y-3 scroll-mt-24">
              <h2 className="text-2xl font-semibold">
                21. Contact
              </h2>

              <p className="text-[#475569] ">
                For questions about these Terms, to report a concern, or to exercise any rights under applicable data protection law, contact:<br /><br />
                OneRoof Launchpad<br />
                Netherlands<br /><br />
                General contact: <a href="mailto:info@launchradar.live" className="text-blue-500 underline">info@launchradar.live</a><br /><br />
                By ticking "I agree to the Terms and Conditions" at sign-up, you confirm that you have read, understood, and agree to these Terms.

              </p>
            </section>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E8F0] py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-[#64748B]">
          <p>© 2026 LaunchRadar. All rights reserved.</p>

          <div className="flex items-center gap-6">
            <Link
              to="/privacy-policy"
              className="hover:text-[#0F1F3D]"
            >
              Privacy Policy
            </Link>

            <Link
              to="/terms-and-conditions"
              className="text-[#0F1F3D] font-medium"
            >
              Terms & Conditions
            </Link>

            <Link
              to="/faq"
              className="hover:text-[#0F1F3D]"
            >
              FAQs
            </Link>

            <Link
              to="/contact"
              className="hover:text-[#0F1F3D]"
            >
              Contact
            </Link>
          </div>
        </div>
      </footer>

      {showWaitlist && (
        <WaitlistModal onClose={() => setShowWaitlist(false)} />
      )}
    </div>
  );
}